"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  OneTimePadReuse,
  type ReuseLabels,
} from "@/components/tools/ciphers-misc/one-time-pad-reuse";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { ALPHABET_SIZE, mod } from "@/lib/tool-kit/ciphers/alphabet";
import {
  applyPad,
  inFives,
  type PadResult,
  padLetters,
  randomPad,
} from "@/lib/tool-kit/ciphers-misc/one-time-pad";

const ROWS_SHOWN = 24;
const MAX_PAD = 5000;
const DEFAULT_PAD_LENGTH = "60";

/** English strings. A translated content file overrides these through `ui`. */
export const ONE_TIME_PAD_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Message",
  cipherText: "Cipher text",
  decodedText: "Decoded message",
  inputPlaceholder: "Type or paste your message",
  sample: "HELLO",
  samplePad: "XMCKL",
  pad: "Pad (letters A–Z)",
  padHint:
    "Spaces and digits are ignored. This starting pad is a textbook example, not random, and it is on this page for anyone to see: press Generate for a fresh one.",
  padLength: "Pad length (letters)",
  generate: "Generate a random pad",
  copyPad: "Copy pad",
  start: "Start at pad letter",
  startHint:
    "Each part of a pad may be used only once. Move this on after every message.",
  usage: "Uses pad letters {from}–{to}. The next unused letter is {next}.",
  nextUnused: "Start the next message at letter {next}",
  group: "Write the cipher text in groups of five",
  padShort:
    "one: The pad is {count} letter too short. A pad must be at least as long as the message.|other: The pad is {count} letters too short. A pad must be at least as long as the message.",
  noPad: "Enter a pad or press Generate.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "Letter by letter",
  workingEncode:
    "Each message letter and pad letter becomes a number (A = 0, B = 1 … Z = 25). The two numbers are added and the total is reduced modulo 26, which means 26 is subtracted if the total is 26 or more.",
  workingDecode:
    "Each cipher letter and pad letter becomes a number (A = 0 … Z = 25). The pad number is subtracted and 26 is added back if the result is below 0.",
  colMessage: "Message",
  colCipher: "Cipher",
  colNumber: "Number",
  colPad: "Pad",
  colPadNumber: "Pad number",
  colSum: "Sum, then mod 26",
  colDifference: "Difference, then mod 26",
  colResult: "Result",
  reduced: "{raw} → {value}",
  rowsShown:
    "one: Showing the first letter.|other: Showing the first {count} letters.",
  emptyWorking: "Type a message and a pad to see the working.",
  reuseToggle: "Show what goes wrong when a pad is reused",
  reuseHeading: "Reusing a pad",
  reuseIntro:
    "Encrypt a second message with the same pad letters. The pad cancels out when the two cipher texts are subtracted, leaving the difference between the two messages, which has patterns an analyst can work on.",
  reuseSecond: "A second message, encrypted with the same pad letters",
  reuseSecondSample: "WORLD",
  reuseCrib: "A word you think is in the first message",
  reuseCribSample: "HELLO",
  reuseCribHint:
    "Wherever the guess fits, the second message is revealed at that spot.",
  reuseCipherOne: "Cipher text 1",
  reuseCipherTwo: "Cipher text 2",
  reuseCipherDifference: "Cipher 1 − cipher 2",
  reuseMessageDifference: "Message 1 − message 2",
  reuseWhat: "Letters of",
  reuseLetters: "Letters",
  reuseSame:
    "The two rows are identical: the pad has dropped out, so the cipher texts alone reveal how the two messages differ.",
  reuseCribHeading: "Dragging a guessed word along",
  reuseCribIntro:
    "The guess is laid at each position of the first message, and the second message is worked out there. The most English-looking results come first.",
  reusePosition: "Position",
  reuseReads: "Second message would read",
  reuseNone: "Enter a guess to try it along the message.",
} as const;

type Strings = typeof ONE_TIME_PAD_STRINGS;

function reuseLabels(strings: Strings): ReuseLabels {
  return {
    heading: strings.reuseHeading,
    intro: strings.reuseIntro,
    secondLabel: strings.reuseSecond,
    cribLabel: strings.reuseCrib,
    cribHint: strings.reuseCribHint,
    cipherOne: strings.reuseCipherOne,
    cipherTwo: strings.reuseCipherTwo,
    cipherDifference: strings.reuseCipherDifference,
    messageDifference: strings.reuseMessageDifference,
    same: strings.reuseSame,
    cribHeading: strings.reuseCribHeading,
    cribIntro: strings.reuseCribIntro,
    colWhat: strings.reuseWhat,
    colLetters: strings.reuseLetters,
    colPosition: strings.reusePosition,
    colReads: strings.reuseReads,
    cribNone: strings.reuseNone,
  };
}

interface NoticesProps {
  strings: Strings;
  padText: string;
  lettersCount: number;
  result: PadResult;
  start: number;
  onStart: (value: string) => void;
}

function PadNotices({
  strings,
  padText,
  lettersCount,
  result,
  start,
  onStart,
}: NoticesProps) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const next = start + lettersCount + 1;
  const complete = result.shortBy === 0;
  return (
    <div className="space-y-2">
      {padText.length === 0 ? <Notice>{strings.noPad}</Notice> : null}
      {result.shortBy > 0 && padText.length > 0 ? (
        <ErrorNotice>
          {shortText(strings, locale, fmt, result.shortBy)}
        </ErrorNotice>
      ) : null}
      {complete && lettersCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className={HINT}>
            {t(strings.usage, {
              from: fmt(start + 1),
              to: fmt(start + lettersCount),
              next: fmt(next),
            })}
          </p>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => onStart(String(next))}
            type="button"
          >
            {t(strings.nextUnused, { next: fmt(next) })}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function shortText(
  strings: Strings,
  locale: string,
  fmt: (value: number) => string,
  count: number
): string {
  return tPlural(strings.padShort, locale, count, { count: fmt(count) });
}

function PadWorking({
  strings,
  result,
  encoding,
}: {
  strings: Strings;
  result: PadResult;
  encoding: boolean;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (result.shortBy > 0 || result.steps.length === 0) {
    return <Notice>{strings.emptyWorking}</Notice>;
  }
  const rows = result.steps.slice(0, ROWS_SHOWN).map((step) => [
    step.input,
    fmt(step.inputValue),
    step.key,
    fmt(step.keyValue),
    t(strings.reduced, {
      raw: fmt(step.raw),
      value: fmt(mod(step.raw, ALPHABET_SIZE)),
    }),
    step.output,
  ]);
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm leading-relaxed">
        {encoding ? strings.workingEncode : strings.workingDecode}
      </p>
      <DataTable
        head={[
          encoding ? strings.colMessage : strings.colCipher,
          strings.colNumber,
          strings.colPad,
          strings.colPadNumber,
          encoding ? strings.colSum : strings.colDifference,
          strings.colResult,
        ]}
        rows={rows}
      />
      {result.steps.length > ROWS_SHOWN ? (
        <p className={HINT}>
          {tPlural(strings.rowsShown, locale, ROWS_SHOWN, {
            count: fmt(ROWS_SHOWN),
          })}
        </p>
      ) : null}
    </div>
  );
}

export default function OneTimePadWidget() {
  const strings = useToolUi(ONE_TIME_PAD_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [pad, setPad] = useState<string>(strings.samplePad);
  const [lengthText, setLengthText] = useState(DEFAULT_PAD_LENGTH);
  const [startText, setStartText] = useState("1");
  const [group, setGroup] = useState(true);
  const [reuse, setReuse] = useState(false);
  const [second, setSecond] = useState<string>(strings.reuseSecondSample);
  const [crib, setCrib] = useState<string>(strings.reuseCribSample);

  const padText = padLetters(pad);
  const letters = padLetters(input);
  const start = Math.max(1, parseIntegerInput(startText) ?? 1) - 1;
  const encoding = mode === "encode";
  const result = applyPad(letters, padText, start, encoding ? 1 : -1);
  const raw = result.shortBy === 0 ? result.output : "";
  const output = encoding && group ? inFives(raw) : raw;
  const messageForDemo = encoding ? letters : result.output;

  function generate() {
    const length = Math.min(
      MAX_PAD,
      Math.max(1, parseIntegerInput(lengthText) ?? 1)
    );
    setPad(inFives(randomPad(length)));
    setStartText("1");
  }

  const keyControls = (
    <div className="space-y-4">
      <TextAreaField
        hint={strings.padHint}
        id="otp-pad"
        label={strings.pad}
        onChange={setPad}
        rows={3}
        value={pad}
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <TextField
            id="otp-length"
            label={strings.padLength}
            numeric
            onChange={setLengthText}
            value={lengthText}
          />
        </div>
        <button className={BUTTON_SECONDARY} onClick={generate} type="button">
          {strings.generate}
        </button>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyPad}
          text={pad}
        />
      </div>
      <div className="w-40">
        <TextField
          hint={strings.startHint}
          id="otp-start"
          label={strings.start}
          numeric
          onChange={setStartText}
          value={startText}
        />
      </div>
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const extras = (
    <div className="space-y-4">
      <Checkbox
        checked={reuse}
        label={strings.reuseToggle}
        onChange={setReuse}
      />
      {reuse ? (
        <OneTimePadReuse
          crib={crib}
          fmt={fmt}
          labels={reuseLabels(strings)}
          messageOne={messageForDemo}
          onCrib={setCrib}
          onSecond={setSecond}
          pad={padText}
          second={second}
          shortText={(count) => shortText(strings, locale, fmt, count)}
          start={start}
        />
      ) : null}
    </div>
  );

  return (
    <CipherWorkbench
      extras={extras}
      idPrefix="otp-io"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        <PadNotices
          lettersCount={letters.length}
          onStart={setStartText}
          padText={padText}
          result={result}
          start={start}
          strings={strings}
        />
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      working={
        <PadWorking encoding={encoding} result={result} strings={strings} />
      }
    />
  );
}
